/**
 * Extra text tab — the clip's timed on-screen lines (the brand's CTA).
 *
 * Every write REPLACES the whole list (`overlaysEdit`'s patch contract, not
 * a per-entry merge), which is why this tab is only ever mounted with a list
 * the caller actually TRACKS. `FocusPanel` holds that gate: `undefined`
 * means "this host doesn't carry the overlays", and adding a line there
 * would commit a replace that silently drops the ones it never showed.
 */
import { Plus, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import type { EditPatch, OverlaysLayout } from "../../../../../src/lib/clipEditState.ts";
import type { RenderTextOverlay } from "../../../../../src/remotion/types.ts";
import { addOverlay, isFromEnd, labelSnippet, removeOverlay, setStartMode, startSeconds, updateOverlay } from "../../../lib/overlaysEdit.ts";
import type { StudioInspectorProps } from "../../StudioInspector.tsx";
import { fieldLabelStyle } from "../../styles.ts";
import { SegmentedRow, SliderRow } from "../essentials/index.ts";

const OVERLAY_TEXT_MAX = 140;

export function ExtraTextTab({ inspector, overlays }: {
  inspector: StudioInspectorProps;
  /** The COMPLETE current list — never a partial view (see the file note). */
  overlays: OverlaysLayout;
}): React.JSX.Element {
  const { brand, onPreview, onCommit } = inspector;
  const dir = (brand.direction ?? "rtl") === "rtl" ? "rtl" : "ltr";
  return (
    <>
      {overlays.length === 0 && (
        <span style={{ fontSize: 12, color: "var(--ql-fg-muted)", lineHeight: 1.55 }}>
          Timed text burned into the video — a call to action, a handle, a reminder. Nothing on screen yet.
        </span>
      )}
      {overlays.map((o, i) => (
        <OverlayRow key={i} index={i} overlay={o} list={overlays} dir={dir} onPreview={onPreview} onCommit={onCommit} />
      ))}
      <button
        type="button"
        onClick={() => onCommit({ layout: { overlays: addOverlay(overlays) } }, "Extra text added")}
        style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
          padding: "8px 10px", borderRadius: 9, cursor: "pointer", fontFamily: "inherit",
          fontSize: 12, fontWeight: 700,
          border: "1px solid rgba(255,213,0,.35)", background: "rgba(255,213,0,.06)", color: "var(--ql-yellow)",
        }}
      >
        <Plus size={13} /> Add a line
      </button>
    </>
  );
}

/** One line of extra text. Takes the whole list plus the two writers rather
 *  than assembling a patch from a slice — replace semantics demand it. */
function OverlayRow({ index, overlay, list, dir, onPreview, onCommit }: {
  index: number;
  overlay: RenderTextOverlay;
  list: OverlaysLayout;
  dir: "rtl" | "ltr";
  onPreview: (p: EditPatch) => void;
  onCommit: (p: EditPatch, label: string) => void;
}): React.JSX.Element {
  const [draft, setDraft] = useState(overlay.text);
  const [editing, setEditing] = useState(false);
  const startRef = useRef(overlay.text);
  const fromEnd = isFromEnd(overlay.start);
  const sec = startSeconds(overlay.start);
  const write = (patch: Partial<RenderTextOverlay>): EditPatch => ({ layout: { overlays: updateOverlay(list, index, patch) } });
  const commitOne = (patch: Partial<RenderTextOverlay>, label: string): void =>
    onCommit(write(patch), `Extra text ${index + 1} ${label}`);

  // External resync (undo, a sibling removal re-indexing this row) — only
  // while the box is idle, so a repaint can never clobber the live caret.
  useEffect(() => {
    if (!editing) setDraft(overlay.text);
  }, [overlay.text, editing]);

  return (
    <div style={{ display: "grid", gap: 10, padding: 10, borderRadius: 11, border: "1px solid var(--ql-border)", background: "rgba(255,255,255,.02)", minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ ...fieldLabelStyle, marginBottom: 0 }}>Line {index + 1}</span>
        <button
          type="button"
          onClick={() => onCommit({ layout: { overlays: removeOverlay(list, index) } }, `Extra text ${index + 1} removed`)}
          title={`Remove line ${index + 1} — one undoable step`}
          aria-label={`Remove line ${index + 1}`}
          style={{
            marginInlineStart: "auto", display: "inline-flex", alignItems: "center", padding: "4px 7px", borderRadius: 7,
            cursor: "pointer", fontFamily: "inherit",
            border: "1px solid var(--ql-border)", background: "rgba(255,255,255,.03)", color: "var(--ql-fg-subtle)",
          }}
        >
          <Trash2 size={11} />
        </button>
      </div>
      <input
        type="text"
        value={draft}
        dir={dir}
        maxLength={OVERLAY_TEXT_MAX}
        aria-label={`Line ${index + 1} text`}
        onFocus={() => { setEditing(true); startRef.current = overlay.text; }}
        onChange={(e) => { setDraft(e.target.value); onPreview(write({ text: e.target.value })); }}
        onBlur={() => {
          setEditing(false);
          const next = draft.trim();
          // An empty line renders nothing and `parseOverlays` drops it —
          // revert rather than persist an entry the render will discard.
          if (!next) {
            setDraft(startRef.current);
            onPreview(write({ text: startRef.current }));
            return;
          }
          setDraft(next);
          if (next !== startRef.current) {
            startRef.current = next;
            commitOne({ text: next }, `→ “${labelSnippet(next, 24)}”`);
          } else {
            onPreview(write({ text: next }));
          }
        }}
        style={{
          width: "100%", boxSizing: "border-box", padding: "7px 9px", borderRadius: 8,
          border: "1px solid var(--ql-border)", background: "rgba(255,255,255,.03)",
          color: "var(--ql-fg)", fontFamily: "inherit", fontSize: 12.5, outline: "none",
        }}
      />
      <SliderRow
        label="Size" value={overlay.fontSizePx} min={20} max={160} step={2} unit="px"
        toPatch={(v) => write({ fontSizePx: v })} commitLabel={`Extra text ${index + 1} size`}
        onPreview={onPreview} onCommit={onCommit}
      />
      <SliderRow
        label="Height" value={overlay.yPct} min={0} max={95} step={0.5} unit="%"
        toPatch={(v) => write({ yPct: v })} commitLabel={`Extra text ${index + 1} height`}
        onPreview={onPreview} onCommit={onCommit}
      />
      <SegmentedRow
        label="When"
        value={fromEnd ? "end" : "start"}
        options={[{ id: "start", label: "From start" }, { id: "end", label: "Before the end" }]}
        // A 0s "before the end" starts exactly AT the end — invisible; the
        // flip floors at 1s the way the seconds slider does.
        toPatch={(v) => write({ start: setStartMode(v === "end" ? Math.max(1, sec) : sec, v as "start" | "end") })}
        commitLabel={`Extra text ${index + 1} timing`}
        onPreview={onPreview} onCommit={onCommit}
      />
      <SliderRow
        label={fromEnd ? "Seconds before the end" : "Seconds after the start"}
        value={sec} min={fromEnd ? 1 : 0} max={60} step={0.5} unit="s"
        toPatch={(v) => write({ start: fromEnd ? { fromEnd: v } : v })}
        commitLabel={`Extra text ${index + 1} timing`}
        onPreview={onPreview} onCommit={onCommit}
      />
    </div>
  );
}
